import { Button } from '@vk-cloud-solutions/marketing-ui';
import axios from 'axios';

import { FormCheckbox } from '~components/Forms/FormCheckbox';
import { FormField } from '~components/Forms/FormField';
import { FormInput } from '~components/Forms/FormInput';
import { FormText } from '~components/Forms/FormText';
import { PopupForm } from '~components/PopupForm/PopupForm';
import { env } from '~constants/env';
import {
  buildFormFields,
  FieldKey,
  getRequiredMark,
} from '~helpers/forms/buildFormFields';
import { SalesUtils } from '~helpers/forms/SalesUtils';
import { prepareFosData } from '~helpers/fos/prepareFosData';
import { getUtmFromUrl } from '~helpers/getUtmFromUrl';
import { StringUtils } from '~helpers/StringUtils';
import { useForm } from '~hooks/useForm';
import { LocationUTM } from '~types/Location.types';
import { URLS } from '~types/Urls.types';

import { FormConsultTypes } from './FormConsult.types';

const fosBatut = env.blogConfig?.fosBatut;

export const FormConsult = ({
  active,
  onClose,
  formLabel,
  logo,
  onSuccess = () => undefined,
}: FormConsultTypes) => {
  const mark = (field: FieldKey) => getRequiredMark(fosBatut, field);
  const fields = buildFormFields(fosBatut);
  const {
    handleSubmit,
    handleChange,
    status,
    setStatus,
    isSubmitting,
    errors,
    values,
  } = useForm({
    onSuccess,
    fields,
    validate(fieldsValues) {
      const comment = SalesUtils.getComment({
        formLabel,
        comment: fieldsValues.comment,
      });

      if (StringUtils.size(comment) > 4096) {
        return {
          comment: 'Текст сообщения слишком длинный',
        };
      }
    },
    submit: async (data) => {
      const comment = SalesUtils.getComment({
        formLabel,
        comment: data.comment,
      });
      const utm = getUtmFromUrl({
        values: [
          LocationUTM.UtmSource,
          LocationUTM.UtmMedium,
          LocationUTM.UtmCampaign,
          LocationUTM.Gclid,
        ],
      });
      const requestBody = {
        metrics: utm,
        info: {
          comment,
          name: data.name,
          email: data.email,
          company: data.company,
          phone: data.phone.replace(/\D/gm, ''),
        },
      };

      try {
        await axios.post(
          fosBatut?.request?.url || URLS.FosBatut,
          prepareFosData({
            bitrix: {
              sourceId: 1,
              descriptionId: 933,
              comment: `comment=[${requestBody.info.comment}]`,
            },
            fields: { ...requestBody.info },
            title: fosBatut?.data?.title || '',
          }),
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        SalesUtils.sendGoals();

        setStatus({
          isSubmitting: false,
          success: {
            message: 'Спасибо за заявку, мы с Вами свяжемся в ближайшее время.',
          },
        });
      } catch (error) {
        setStatus({
          isSubmitting: false,
          error: {
            message: 'Произошла ошибка. Мы уже работаем над этим.',
          },
        });
      }
    },
  });

  return (
    <PopupForm
      active={active}
      title={status?.success ? 'Заявка принята' : 'Получить консультацию'}
      isLoading={isSubmitting}
      success={status?.success}
      error={status?.error}
      onClose={onClose}
      logo={logo}
    >
      <form onSubmit={handleSubmit} noValidate>
        <FormField errors={errors.name}>
          <FormInput
            placeholder={`Как к вам можно обращаться?${mark('name')}`}
            name="name"
            value={values.name}
            isError={!!errors.name}
            onChange={handleChange}
          />
        </FormField>
        <FormField errors={errors.email}>
          <FormInput
            placeholder={`E-mail для получения ответа${mark('email')}`}
            name="email"
            type="email"
            value={values.email}
            isError={!!errors.email}
            onChange={handleChange}
          />
        </FormField>
        <FormField errors={errors.phone}>
          <FormInput
            placeholder={`Телефон${mark('phone')}`}
            name="phone"
            type="tel"
            value={values.phone}
            isError={!!errors.phone}
            onChange={handleChange}
            mask="+7 (999) 999-99-99"
          />
        </FormField>
        <FormField errors={errors.company}>
          <FormInput
            placeholder={`Компания${mark('company')}`}
            name="company"
            value={values.company}
            isError={!!errors.company}
            onChange={handleChange}
          />
        </FormField>
        <FormField errors={errors.comment}>
          <FormText
            rows={4}
            placeholder={`Комментарий${mark('comment')}`}
            name="comment"
            value={values.comment}
            isError={!!errors.comment}
            onChange={handleChange}
          />
        </FormField>
        <FormField errors={errors.privacy}>
          <FormCheckbox
            name="privacy"
            value={values.privacy}
            checked={!!values.privacy}
            isError={!!errors.privacy}
            onChange={handleChange}
          />
        </FormField>
        <Button mod="primary" type="submit" text={'Отправить'} />
      </form>
    </PopupForm>
  );
};
